import { DeviceStatusInfo } from "@fairfleet/geotab/types";
import { useMemo } from "react";
import {
  MapAssetRow,
  clampPercent,
  diagnosticValues,
  driveTypeOf,
  energyPercentOf,
  statusDurationSeconds,
} from "../models/mapAssetRow";
import {
  DIAGNOSTIC_CHARGING_STATE_ID,
  DIAGNOSTIC_DEF_LEVEL_ID,
  DIAGNOSTIC_FUEL_LEVEL_ID,
  DIAGNOSTIC_STATE_OF_CHARGE_ID,
} from "@/constants/constants";
import { deviceActivity } from "@/utilities/deviceActivity";

interface Options {
  /** The vehicles to describe. */
  deviceIds: string[];
  names: Record<string, string | undefined>;
  statusInfoMap: Record<string, DeviceStatusInfo>;
  /**
   * When the status poll last came back. In the dependencies so that the durations move on with
   * every poll — react-query hands back the same object when nothing changed, and a memo keyed
   * only on the data would leave every duration frozen at the value it had on the first poll.
   */
  statusInfoMapUpdatedAt?: number;
  /** Driving state as the map displays it, already smoothed against flicker. */
  drivingById: Record<string, boolean>;
  /**
   * The hydrogen vehicles. Nothing a vehicle reports says it runs on hydrogen — a fuel cell has a
   * battery too and reports a charge level for it — so it is known from the group it is in.
   */
  fuelCellDeviceIds: ReadonlySet<string>;
}

/**
 * What the map knows about each visible vehicle, in one array.
 *
 * Both the name labels and the vehicle list read from this. Deriving it twice is how the copy this
 * is ported from ended up with a charging bolt that worked on the label and never appeared in the
 * list.
 *
 * A vehicle with no status record yet still gets a row, with only its name and whether it moves.
 * That happens for every vehicle the map has drawn inside a cluster: those are in the list, but
 * their diagnostics are not polled until they are drawn on their own.
 *
 * Addresses are deliberately not in here. They are a reverse-geocoding request per vehicle and
 * only the list ever shows them, so the list fetches them for the rows it is about to draw.
 */
export function useMapAssetRows({
  deviceIds,
  names,
  statusInfoMap,
  statusInfoMapUpdatedAt,
  drivingById,
  fuelCellDeviceIds,
}: Options): MapAssetRow[] {
  return useMemo(() => {
    const now = Date.now();

    return deviceIds.map(deviceId => {
      const info = statusInfoMap[deviceId];
      const readings = diagnosticValues(info);
      const soc = clampPercent(readings.get(DIAGNOSTIC_STATE_OF_CHARGE_ID));
      const fuelLevel = clampPercent(readings.get(DIAGNOSTIC_FUEL_LEVEL_ID));
      // Geotab reports 0 for not charging and 1-3 for the ways it can be charging.
      const isCharging = (readings.get(DIAGNOSTIC_CHARGING_STATE_ID) ?? 0) > 0;
      const driveType = driveTypeOf({
        soc,
        isCharging,
        isFuelCell: fuelCellDeviceIds.has(deviceId),
      });

      return {
        deviceId,
        name: names[deviceId] ?? deviceId,
        isDriving: drivingById[deviceId] ?? false,
        isOffline: deviceActivity(info) === "offline",
        isCharging,
        driveType,
        energyPercent: energyPercentOf({ driveType, soc, fuelLevel }),
        soc,
        fuelLevel,
        adBlueLevel: clampPercent(readings.get(DIAGNOSTIC_DEF_LEVEL_ID)),
        // The same field the info panel shows, so the two panels cannot disagree about a
        // vehicle's speed while both are open.
        speed: typeof info?.speed === "number" ? info.speed : undefined,
        durationSeconds: statusDurationSeconds(info?.currentStateDuration, info?.dateTime, now),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIds, names, statusInfoMap, statusInfoMapUpdatedAt, drivingById, fuelCellDeviceIds]);
}
